import React, { useCallback, useEffect, useState } from 'react';
import { Colors as C } from '../../constants/Colors.js';
import {
  buyerDeliveryModeLabel,
  formatShippingAddressOneLine,
  SELLER_DROPOFF_STATUSES,
} from '../constants/transactionLogistics.js';
import {
  fetchListingTransactionMessages,
  sendListingTransactionMessage,
} from '../../lib/listingTransactionMessagesService.js';
import {
  sellerSetTransactionDropoff,
  partnerSetTransactionOutbound,
} from '../../lib/transactionLogisticsService.js';
import { sellerModeLabel } from '../constants/partnerLogistics.js';

const panelStyle = {
  marginTop: 10,
  padding: '12px 14px',
  borderRadius: 10,
  border: '0.5px solid rgba(255,255,255,0.1)',
  background: 'rgba(0,0,0,0.25)',
};

const inputStyle = {
  width: '100%',
  padding: '8px 10px',
  borderRadius: 8,
  border: '0.5px solid rgba(255,255,255,0.12)',
  background: 'rgba(255,255,255,0.05)',
  color: '#fff',
  fontSize: 11,
  boxSizing: 'border-box',
};

/**
 * Hilo operativo por transacción: logística v2 + mensajes (no chat general).
 * @param {'seller'|'buyer'|'partner'} viewerRole
 */
export default function TransactionShipmentPanel({
  transaction,
  viewerRole = 'buyer',
  currentUserId,
  onUpdated,
  title = 'Mensajes de este envío',
}) {
  const tx = transaction;
  const txId = tx?.id;
  const [messages, setMessages] = useState([]);
  const [msgLoading, setMsgLoading] = useState(false);
  const [msgDraft, setMsgDraft] = useState('');
  const [msgBusy, setMsgBusy] = useState(false);
  const [dropoffDraft, setDropoffDraft] = useState('');
  const [dropoffBusy, setDropoffBusy] = useState(false);
  const [outboundDraft, setOutboundDraft] = useState('');
  const [outboundBusy, setOutboundBusy] = useState(false);
  const [err, setErr] = useState('');

  const hub = !!tx?.buyer_partner_delivery_mode || !!tx?.verifier_partner_id || !!tx?.pickup_partner_id;
  const buyerMode = tx?.buyer_partner_delivery_mode;
  const addrLine = formatShippingAddressOneLine(tx?.buyer_shipping_address);

  const loadMessages = useCallback(async () => {
    if (!txId) return;
    setMsgLoading(true);
    setErr('');
    const { messages: rows, error } = await fetchListingTransactionMessages(txId);
    if (error) setErr(error);
    else setMessages(rows);
    setMsgLoading(false);
  }, [txId]);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  useEffect(() => {
    setDropoffDraft(tx?.seller_dropoff_tracking || '');
    setOutboundDraft(tx?.partner_outbound_tracking || '');
  }, [tx?.seller_dropoff_tracking, tx?.partner_outbound_tracking]);

  const sendMessage = async () => {
    const text = msgDraft.trim();
    if (!txId || !text) return;
    setMsgBusy(true);
    setErr('');
    const { error } = await sendListingTransactionMessage(txId, text, 'text');
    setMsgBusy(false);
    if (error) {
      setErr(error);
      return;
    }
    setMsgDraft('');
    await loadMessages();
  };

  const saveDropoff = async () => {
    if (!txId) return;
    setDropoffBusy(true);
    setErr('');
    const { error } = await sellerSetTransactionDropoff(txId, {
      tracking: dropoffDraft.trim() || null,
      status: dropoffDraft.trim() ? 'shipped' : 'pending',
    });
    setDropoffBusy(false);
    if (error) {
      setErr(error);
      return;
    }
    onUpdated?.();
    await loadMessages();
  };

  const saveOutbound = async () => {
    if (!txId) return;
    setOutboundBusy(true);
    setErr('');
    const { error } = await partnerSetTransactionOutbound(txId, outboundDraft.trim());
    setOutboundBusy(false);
    if (error) {
      setErr(error);
      return;
    }
    onUpdated?.();
    await loadMessages();
  };

  if (!txId) {
    return (
      <div style={{ ...panelStyle, fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>
        Sin transacción vinculada.
      </div>
    );
  }

  return (
    <div style={panelStyle}>
      <div style={{ fontSize: 11, fontWeight: 600, color: C.gold, marginBottom: 8 }}>{title}</div>

      {hub && buyerMode ? (
        <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)', lineHeight: 1.5, marginBottom: 10 }}>
          <div>
            Entrega al comprador: <strong style={{ color: 'rgba(255,255,255,0.85)' }}>{buyerDeliveryModeLabel(buyerMode)}</strong>
          </div>
          {buyerMode === 'ship_to_buyer' && addrLine ? (
            <div style={{ marginTop: 4 }}>
              Dirección: <span style={{ color: 'rgba(255,255,255,0.75)' }}>{addrLine}</span>
            </div>
          ) : null}
          {tx.seller_to_partner_mode ? (
            <div style={{ marginTop: 4 }}>
              Vendedor → partner: {sellerModeLabel(tx.seller_to_partner_mode)}
              {tx.seller_dropoff_status ? ` · ${SELLER_DROPOFF_STATUSES[tx.seller_dropoff_status] || tx.seller_dropoff_status}` : ''}
              {tx.seller_dropoff_tracking ? ` · ${tx.seller_dropoff_tracking}` : ''}
            </div>
          ) : null}
          {tx.partner_outbound_tracking ? (
            <div style={{ marginTop: 4 }}>
              Partner → comprador: {tx.partner_outbound_tracking}
            </div>
          ) : null}
        </div>
      ) : null}

      {viewerRole === 'seller' && tx.seller_to_partner_mode === 'shipping' ? (
        <div style={{ marginBottom: 12, paddingBottom: 10, borderBottom: '0.5px solid rgba(255,255,255,0.08)' }}>
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginBottom: 6 }}>
            Tracking vendedor → partner
          </div>
          <input
            type="text"
            value={dropoffDraft}
            onChange={e => setDropoffDraft(e.target.value)}
            placeholder="Nº seguimiento mensajería al partner"
            style={{ ...inputStyle, marginBottom: 8 }}
          />
          <button
            type="button"
            disabled={dropoffBusy}
            onClick={() => void saveDropoff()}
            style={{
              padding: '6px 12px',
              borderRadius: 8,
              border: 'none',
              background: C.gold,
              color: '#0a0a0f',
              fontSize: 10,
              fontWeight: 700,
              cursor: dropoffBusy ? 'wait' : 'pointer',
            }}
          >
            {dropoffBusy ? 'Guardando…' : 'Guardar envío al partner'}
          </button>
        </div>
      ) : null}

      {viewerRole === 'partner' && buyerMode === 'ship_to_buyer' ? (
        <div style={{ marginBottom: 12, paddingBottom: 10, borderBottom: '0.5px solid rgba(255,255,255,0.08)' }}>
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginBottom: 6 }}>
            Tracking partner → comprador
          </div>
          <input
            type="text"
            value={outboundDraft}
            onChange={e => setOutboundDraft(e.target.value)}
            placeholder="Nº seguimiento al comprador"
            style={{ ...inputStyle, marginBottom: 8 }}
          />
          <button
            type="button"
            disabled={outboundBusy || !outboundDraft.trim()}
            onClick={() => void saveOutbound()}
            style={{
              padding: '6px 12px',
              borderRadius: 8,
              border: 'none',
              background: C.gold,
              color: '#0a0a0f',
              fontSize: 10,
              fontWeight: 700,
              cursor: outboundBusy ? 'wait' : 'pointer',
            }}
          >
            {outboundBusy ? 'Guardando…' : 'Registrar envío al comprador'}
          </button>
        </div>
      ) : null}

      {err ? <div style={{ fontSize: 10, color: C.warn, marginBottom: 8 }}>{err}</div> : null}

      <div
        style={{
          maxHeight: 160,
          overflowY: 'auto',
          marginBottom: 10,
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
        }}
      >
        {msgLoading ? (
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)' }}>Cargando mensajes…</div>
        ) : messages.length === 0 ? (
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)' }}>
            Sin mensajes aún. Usa este hilo para incidencias o avisos de envío (no sustituye el chat con el vendedor).
          </div>
        ) : (
          messages.map(m => {
            const mine = m.sender_id === currentUserId;
            const isSystem = m.kind === 'system' || m.kind === 'tracking' || m.kind === 'status';
            return (
              <div
                key={m.id}
                style={{
                  alignSelf: mine ? 'flex-end' : 'flex-start',
                  maxWidth: '92%',
                  padding: '6px 10px',
                  borderRadius: 8,
                  background: isSystem
                    ? 'rgba(83,74,183,0.2)'
                    : mine
                      ? 'rgba(200,168,75,0.15)'
                      : 'rgba(255,255,255,0.06)',
                  fontSize: 10,
                  color: 'rgba(255,255,255,0.88)',
                  lineHeight: 1.45,
                }}
              >
                {m.body}
              </div>
            );
          })
        )}
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <input
          type="text"
          value={msgDraft}
          onChange={e => setMsgDraft(e.target.value)}
          placeholder="Escribe un mensaje sobre este envío…"
          style={{ ...inputStyle, flex: 1 }}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void sendMessage();
            }
          }}
        />
        <button
          type="button"
          disabled={msgBusy || !msgDraft.trim()}
          onClick={() => void sendMessage()}
          style={{
            padding: '8px 14px',
            borderRadius: 8,
            border: 'none',
            background: C.gold,
            color: '#0a0a0f',
            fontSize: 10,
            fontWeight: 700,
            cursor: msgBusy ? 'wait' : 'pointer',
            flexShrink: 0,
          }}
        >
          Enviar
        </button>
      </div>
    </div>
  );
}
